import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

// Client-side downscale keeps a modern phone photo (5-8 MB) under ~500 KB by
// the time it hits the wire, which cuts perceived upload time roughly 5-10x
// on residential upload links. Full-res originals were never used by the UI
// (thumbnails are grid-sized and downloads are on-demand), so this is safe.
const MAX_EDGE = 2560;
const JPEG_QUALITY = 0.85;

// Cap concurrent uploads so we don't swamp the single-worker backend or the
// browser's per-origin socket pool. 4 is the sweet spot on residential
// links and matches what most photo apps use.
const UPLOAD_CONCURRENCY = 4;

// Types we can reliably decode into a canvas across evergreen browsers.
// HEIC/HEIF stay untouched and are shipped as-is; the backend accepts them
// and reads EXIF server-side.
const RESIZABLE_MIME = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]);

export const PHOTO_ACCEPT = "image/*";

async function resizeIfLarge(file) {
  if (!RESIZABLE_MIME.has((file.type || "").toLowerCase())) return file;
  if (typeof createImageBitmap !== "function") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const longestEdge = Math.max(bitmap.width, bitmap.height);
    const scale = Math.min(1, MAX_EDGE / longestEdge);
    if (scale >= 1) return file; // Already small enough.
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );
    if (!blob) return file;
    const renamed = file.name.replace(/\.(png|webp)$/i, ".jpg");
    return new File([blob], renamed, {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}

async function uploadOne(projectId, file, onProgress) {
  const prepared = await resizeIfLarge(file);
  const form = new FormData();
  form.append("file", prepared);
  const res = await axios.post(`${API}/projects/${projectId}/photos`, form, {
    onUploadProgress: (event) => {
      if (!onProgress || !event.total) return;
      onProgress(Math.min(1, event.loaded / event.total));
    },
  });
  return res.data;
}

async function withConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  const pump = async () => {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= items.length) return;
      results[index] = await worker(items[index], index);
    }
  };
  const runners = Array.from(
    { length: Math.min(limit, items.length) },
    () => pump(),
  );
  await Promise.all(runners);
  return results;
}

/**
 * Upload photos to a project via the existing tenant-scoped endpoint.
 * Downscales large JPEGs / PNGs / WebPs client-side before upload and runs
 * uploads with a small concurrency window. HEIC/HEIF originals are shipped
 * unchanged so EXIF is preserved for server-side parsing.
 *
 * `onProgress(index, progress)` is called (0..1) as each file uploads.
 */
export async function uploadPhotosToProject(projectId, files, onProgress) {
  return withConcurrency(Array.from(files), UPLOAD_CONCURRENCY, (file, index) =>
    uploadOne(projectId, file, (p) => onProgress && onProgress(index, p)),
  );
}
